import { zodResolver } from "@hookform/resolvers/zod";
import { buildChangedEntryData, NotificationType, useLorebook, useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import {
  Alert,
  AlertVariant,
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Modal,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import {
  LorebookEntry,
  LorebookEntryFixStatus,
  LorebookEntryOpData,
  LorebookEntryStatus,
  PatchLorebookEntryRequest,
  PostLorebookEntryRequest,
} from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import Trans from "@/components/Trans/Trans";
import useToast from "@/hooks/useToast";

import {
  ENTRY_FORM_FIELDS,
  LOREBOOK_ENTRY_LIMIT_MESSAGE,
  MAX_ENTRY_CONTENT_LENGTH,
  MAX_ENTRY_KEYWORD_LENGTH,
  MAX_ENTRY_KEYWORDS_COUNT,
  MAX_ENTRY_NAME_LENGTH,
  MAX_LOREBOOK_ENTRIES,
} from "../constants";
import { IEntryDataFormProps } from "../types";
import { getEntryValidationSchema } from "../validationSchema";
import { FormField } from "./FormField";

const logger = appLogger.withTag("ModalEntry");

interface IModalEntryProps {
  isOpen?: boolean;
  onClose: () => void;
  onSubmit?: (entryData: PostLorebookEntryRequest, index?: number) => void;
  onDelete?: (index: number) => void;
  editIndex?: number;
  entryData?: LorebookEntry;
  lorebookId?: string;
  entriesCount?: number;
  isFormDisabled?: boolean;
  isReadOnly?: boolean;
  // Staged-edit mode (public-lorebook moderation flow, flag-gated). When these are provided, the
  // modal stages the change locally instead of hitting the API immediately; the parent submits the
  // aggregated snapshot later. When absent, the modal uses the legacy immediate-API path unchanged.
  isStagingMode?: boolean;
  onStageEntryCreate?: (entryData: PostLorebookEntryRequest) => void;
  onStageEntryUpdate?: (entryId: string, entryData: LorebookEntryOpData) => void;
  onStageEntryDelete?: (entryId: string) => void;
}

export default function ModalEntry({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  editIndex,
  entryData,
  lorebookId,
  entriesCount,
  isFormDisabled,
  isReadOnly,
  isStagingMode = false,
  onStageEntryCreate,
  onStageEntryUpdate,
  onStageEntryDelete,
}: IModalEntryProps) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const { onUserDeleteLorebookEntry, onUserUpdateLorebookEntry, onUserCreateLorebookEntry } = useLorebook();
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { displayToast } = useToast();
  const isEditing = typeof editIndex === "number" && entryData;
  const hasReachedEntryLimit = (entriesCount ?? 0) >= MAX_LOREBOOK_ENTRIES;
  let modalTitle = t("lorebook:modal.entry.title.add");
  if (isReadOnly) {
    modalTitle = t("lorebook:modal.entry.title.view");
  } else if (isEditing) {
    modalTitle = t("lorebook:modal.entry.title.edit");
  }
  const entryValidationSchema = useMemo(() => getEntryValidationSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<IEntryDataFormProps>({
    resolver: zodResolver(entryValidationSchema as never),
    defaultValues: {
      [ENTRY_FORM_FIELDS.ID]: "",
      [ENTRY_FORM_FIELDS.NAME]: "",
      [ENTRY_FORM_FIELDS.KEYWORDS]: [],
      [ENTRY_FORM_FIELDS.CONTENT]: "",
    },
  });

  // Reset form with entry data when modal opens or entry changes
  useEffect(() => {
    if (isOpen && entryData) {
      reset({
        [ENTRY_FORM_FIELDS.ID]: entryData.id,
        [ENTRY_FORM_FIELDS.NAME]: entryData.name,
        [ENTRY_FORM_FIELDS.KEYWORDS]: entryData.keywords,
        [ENTRY_FORM_FIELDS.CONTENT]: entryData.content,
      });
    } else if (isOpen && !entryData) {
      // Reset to empty values for new entry
      reset({
        [ENTRY_FORM_FIELDS.ID]: "",
        [ENTRY_FORM_FIELDS.NAME]: "",
        [ENTRY_FORM_FIELDS.KEYWORDS]: [],
        [ENTRY_FORM_FIELDS.CONTENT]: "",
      });
    }
  }, [isOpen, entryData, reset]);

  const buildEntryPayload = (formData: IEntryDataFormProps): PatchLorebookEntryRequest & { updatedAt?: string } => {
    const currentTimestamp = new Date().toISOString();
    return {
      name: formData[ENTRY_FORM_FIELDS.NAME] || "",
      keywords: formData[ENTRY_FORM_FIELDS.KEYWORDS] || [],
      content: formData[ENTRY_FORM_FIELDS.CONTENT] || "",
      // Add updatedAt for creation mode (when lorebookId is not provided)
      ...(lorebookId ? {} : { updatedAt: currentTimestamp }),
    } as PatchLorebookEntryRequest & { updatedAt?: string };
  };

  const submitEntryViaApi = async (
    entryPayload: (PatchLorebookEntryRequest & { updatedAt?: string }) | PostLorebookEntryRequest,
  ): Promise<{ resultId?: string; hasError: boolean }> => {
    if (lorebookId && entryData?.id) {
      // Edit mode: updating existing entry via API
      const result = await onUserUpdateLorebookEntry(lorebookId, entryData.id, entryPayload as PatchLorebookEntryRequest);
      if (result.data) {
        displayToast({ message: t("lorebook:toast.entryUpdated"), type: NotificationType.success });
      } else if (result.error) {
        displayToast({ message: result.error, type: NotificationType.error });
        return { hasError: true };
      }
      return { resultId: result.data?.id, hasError: false };
    }

    if (lorebookId) {
      // Edit mode: creating new entry via API
      const result = await onUserCreateLorebookEntry(lorebookId, entryPayload as PostLorebookEntryRequest);
      if (result.data) {
        displayToast({ message: t("lorebook:toast.entryAdded"), type: NotificationType.success });
      } else if (result.error) {
        displayToast({ message: result.error, type: NotificationType.error });
        return { hasError: true };
      }
      return { resultId: result.data?.id, hasError: false };
    }

    // Creation mode (no lorebookId): enrich payload with existing entry id
    if (entryData?.id) {
      Object.assign(entryPayload, { id: entryData.id, updatedAt: new Date().toISOString() });
    }
    return { hasError: false };
  };

  // Staged-edit path: record the create/update locally instead of hitting the API. The payload
  // `{ name, keywords, content }` is a valid create request and a valid entry-op update data.
  const stageEntrySubmit = (entryPayload: PatchLorebookEntryRequest & { updatedAt?: string }) => {
    const { name, keywords, content } = entryPayload;
    if (entryData?.id) {
      // Only stage fields whose value actually changed, so moderation does not show untouched
      // fields as edited.
      const changedData = buildChangedEntryData({ name, keywords, content }, entryData);
      if (Object.keys(changedData).length > 0) {
        onStageEntryUpdate?.(entryData.id, changedData);
      }
    } else {
      onStageEntryCreate?.({ name: name ?? "", keywords: keywords ?? [], content: content ?? "" });
    }
    onSubmit?.(entryPayload as PostLorebookEntryRequest, editIndex);
    reset();
    onClose();
  };

  const handleFormSubmit = async (formData: IEntryDataFormProps) => {
    const currentEntryCount = entriesCount ?? 0;
    // Guard before submitting so we never stage/send changes the backend would reject for limit violations
    if (!isEditing && currentEntryCount >= MAX_LOREBOOK_ENTRIES) {
      displayToast({ message: LOREBOOK_ENTRY_LIMIT_MESSAGE, type: NotificationType.error });
      return;
    }

    const entryPayload = buildEntryPayload(formData);

    if (isStagingMode) {
      stageEntrySubmit(entryPayload);
      return;
    }

    setIsLoading(true);
    const { resultId, hasError } = await submitEntryViaApi(entryPayload);
    setIsLoading(false);

    if (hasError) return;

    if (resultId) {
      entryPayload.id = resultId;
    }
    onSubmit?.(entryPayload as PostLorebookEntryRequest, editIndex);
    reset();
    onClose();
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmationOpen(true);
  };

  const handleDelete = async () => {
    if (typeof editIndex !== "number") return;

    // Staged-edit path: record the delete locally; live data is untouched until submit.
    if (isStagingMode) {
      if (entryData?.id) {
        onStageEntryDelete?.(entryData.id);
      }
      onDelete?.(editIndex);
      reset();
      setIsDeleteConfirmationOpen(false);
      onClose();
      return;
    }

    setIsDeleting(true);
    try {
      if (lorebookId && entryData?.id) {
        await onUserDeleteLorebookEntry(lorebookId, entryData.id);
        displayToast({ message: t("lorebook:toast.entryDeleted"), type: NotificationType.default });
      }

      // Remove from form state
      onDelete?.(editIndex);

      reset();
      setIsDeleteConfirmationOpen(false);
      onClose();
    } catch (error) {
      logger.error("Failed to delete entry:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setIsDeleteConfirmationOpen(false);
    reset();
    onClose();
  };

  const isRejectedAndNotEdited = entryData?.status === LorebookEntryStatus.REJECTED && Object.keys(dirtyFields).length === 0;

  const isSubmitVisuallyDisabled = isDeleting || (!isEditing && !isDirty) || (!isEditing && hasReachedEntryLimit) || isRejectedAndNotEdited;

  const handleRequestSubmit = useCallback(() => {
    if (isEditing && isRejectedAndNotEdited) {
      displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
      return;
    }
    formRef.current?.requestSubmit();
  }, [displayToast, isEditing, isRejectedAndNotEdited, t]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteConfirmationOpen(false);
  }, []);

  const onFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      void handleSubmit(handleFormSubmit)(event);
    },
    [handleSubmit, handleFormSubmit],
  );

  const onConfirmDelete = useCallback(() => {
    void handleDelete();
  }, [handleDelete]);

  const Actions = () => (
    <div className="flex gap-2 w-full justify-between">
      <Button
        variant={ButtonVariant.TEXT}
        size={ButtonSize.LARGE}
        onClick={handleClose}
        disabled={isDeleting || isLoading}
        aria-label={t("lorebook:modal.entry.deleteConfirm.cancel")}
      >
        <Trans i18nKey="lorebook:modal.entry.deleteConfirm.cancel" />
      </Button>
      <div className="flex gap-sm">
        {isEditing && entriesCount && entriesCount > 1 ? (
          <Button
            variant={ButtonVariant.OUTLINED}
            onClick={handleDeleteClick}
            disabled={isDeleting || isLoading || isFormDisabled}
            aria-label={t("lorebook:modal.entry.deleteConfirm.delete")}
          >
            <Trans i18nKey="lorebook:modal.entry.deleteConfirm.delete" />
          </Button>
        ) : null}
        <Button
          variant={ButtonVariant.CONTAINED}
          onClick={handleRequestSubmit}
          aria-disabled={isSubmitVisuallyDisabled}
          className={isSubmitVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
          loading={isLoading}
          aria-label={isEditing ? t("lorebook:modal.entry.action.update") : t("lorebook:modal.entry.action.save")}
          disabled={isFormDisabled}
        >
          {isEditing ? <Trans i18nKey="lorebook:modal.entry.action.update" /> : <Trans i18nKey="lorebook:modal.entry.action.save" />}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal title={modalTitle} open={isOpen} dismissable={!isLoading} onOpenChange={onClose} actions={isReadOnly ? null : <Actions />}>
      {(entryData?.status === LorebookEntryStatus.REJECTED ||
        entryData?.status === LorebookEntryStatus.NEEDS_REVIEW ||
        entryData?.fix_status === LorebookEntryFixStatus.NEEDS_FIX) && (
        <Alert
          variant={AlertVariant.ALERT}
          visible={true}
          isDismissable={false}
          title={t("lorebook:modal.entry.alert.title")}
          description={entryData.fix_reason || t("lorebook:modal.entry.alert.description")}
        />
      )}
      <form ref={formRef} onSubmit={onFormSubmit}>
        <div className="flex flex-col gap-4">
          <FormField
            name={ENTRY_FORM_FIELDS.NAME}
            type="text"
            control={control}
            errors={errors}
            label={t("lorebook:modal.entry.field.name.label")}
            placeholder={t("lorebook:modal.entry.field.name.placeholder")}
            helperText={t("lorebook:modal.entry.field.name.hint")}
            maxLength={MAX_ENTRY_NAME_LENGTH}
            required
            tooltipLabel={t("lorebook:modal.entry.field.name.tooltip.body")}
            disabled={isLoading || isFormDisabled}
            readOnly={isReadOnly}
          />
          <FormField
            name={ENTRY_FORM_FIELDS.KEYWORDS}
            type="keywords"
            control={control}
            errors={errors}
            label={t("lorebook:modal.entry.field.keywords.label")}
            placeholder={t("lorebook:modal.entry.field.keywords.placeholder")}
            helperText={t("lorebook:modal.entry.field.keywords.hint")}
            maxLength={MAX_ENTRY_KEYWORD_LENGTH}
            required
            tooltipLabel={t("lorebook:modal.entry.field.keywords.tooltip.body")}
            maxCount={MAX_ENTRY_KEYWORDS_COUNT}
            disabled={isLoading || isFormDisabled}
            readOnly={isReadOnly}
          />
          <FormField
            name={ENTRY_FORM_FIELDS.CONTENT}
            type="textarea"
            control={control}
            errors={errors}
            label={t("lorebook:modal.entry.field.content.label")}
            placeholder={t("lorebook:modal.entry.field.content.placeholder")}
            helperText={t("lorebook:modal.entry.field.content.hint")}
            maxLength={MAX_ENTRY_CONTENT_LENGTH}
            required
            tooltipLabel={t("lorebook:modal.entry.field.content.tooltip.body")}
            disabled={isLoading || isFormDisabled}
            readOnly={isReadOnly}
          />
        </div>
      </form>
      <Modal
        title={t("lorebook:modal.entry.deleteConfirm.title")}
        open={isDeleteConfirmationOpen}
        onOpenChange={setIsDeleteConfirmationOpen}
        dismissable={!isDeleting}
        actions={
          <div className="flex justify-end w-full gap-sm">
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={handleCancelDelete}
              disabled={isDeleting}
              aria-label={t("lorebook:modal.entry.deleteConfirm.cancelAria")}
            >
              <Trans i18nKey="lorebook:modal.entry.deleteConfirm.cancel" />
            </Button>
            <Button
              variant={ButtonVariant.CONTAINED}
              color={ButtonColorVariant.ERROR}
              onClick={onConfirmDelete}
              loading={isDeleting}
              aria-label={t("lorebook:modal.entry.deleteConfirm.deleteAria")}
              disabled={isFormDisabled}
            >
              <Trans i18nKey="lorebook:modal.entry.deleteConfirm.delete" />
            </Button>
          </div>
        }
      >
        <Text as="p" variant={TextVariants.LABEL_MEDIUM_REGULAR}>
          <Trans i18nKey="lorebook:modal.entry.deleteConfirm.description" />
        </Text>
      </Modal>
    </Modal>
  );
}
